// WebSocket service for real-time notifications

import { NotificationItem } from '../store/notificationSlice';
import { API_BASE_URL } from './api';
import { fetchWithAuthRetry, coordinatedRefresh } from './refreshCoordinator';
import { getWebsocketBaseUrl } from '../utils/urlConfig';
import { DEBUG_CONNECTIVITY, DEBUG_LOGS } from '../utils/debugFlags';
import { store } from '../store';

// Returns true only when Redux says the user is fully authenticated. Reading
// the store directly keeps the singleton's reconnect path from racing past
// hook-level gates after logout / session invalidation.
const isAuthValid = (): boolean => {
  const auth = store.getState().auth;
  return !!auth.isAuthenticated && !!auth.user && auth.validationState === 'valid';
};

type NotificationHandler = (notification: NotificationItem) => void;
type UnreadCountHandler = (count: number) => void;
type ConnectionHandler = (connected: boolean) => void;
type NotificationUpdateHandler = (data: any) => void;

interface WebSocketMessage {
  type: 'notification' | 'notification_update' | 'unread_count' | 'pong' | 'error';
  action?: 'new' | 'update' | 'delete' | 'bulk_update';
  payload?: any;
  notification?: NotificationItem;
  unread_count?: number;
  timestamp?: string;
  message?: string;
}

const WEBSOCKET_BASE_URL = getWebsocketBaseUrl().replace(/\/$/, '');
const DEBUG_NS_VERBOSE = DEBUG_LOGS;

class NotificationWebSocketService {
  private socket: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectInterval = 2000;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private connectionState: 'disconnected' | 'connecting' | 'connected' | 'error' = 'disconnected';
  private isConnecting: boolean = false;
  private isManuallyDisconnected: boolean = false;
  private lastSocketUrl: string = ''; // Store last socket URL for debugging
  
  // Event handlers
  private notificationHandlers: NotificationHandler[] = [];
  private unreadCountHandlers: UnreadCountHandler[] = [];
  private connectionHandlers: ConnectionHandler[] = [];
  private updateHandlers: NotificationUpdateHandler[] = [];
  private stateChangeHandlers: ((state: string) => void)[] = [];

  // Connection state management
  getConnectionState(): string {
    return this.connectionState;
  }
  
  onStateChange(handler: (state: string) => void): () => void {
    this.stateChangeHandlers.push(handler);
    
    return () => {
      const index = this.stateChangeHandlers.indexOf(handler);
      if (index > -1) {
        this.stateChangeHandlers.splice(index, 1);
      }
    };
  }
  
  private setConnectionState(state: 'disconnected' | 'connecting' | 'connected' | 'error'): void {
    this.connectionState = state;
    this.stateChangeHandlers.forEach(handler => {
      try {
        handler(state);
      } catch (error) {
        if (DEBUG_NS_VERBOSE) {
          console.error('[NotificationWS] Error in state change handler:', error);
        }
      }
    });
    if (DEBUG_CONNECTIVITY) {
      console.log('[NotificationWS] Connection state:', state);
    }
  }

  async connect(options: { fromReconnect?: boolean } = {}): Promise<void> {
    // [WS-DEBUG] Always-on: identify caller + state at every connect attempt.
    const callerStack = (new Error('notif-connect-caller').stack || '')
      .split('\n')
      .slice(2, 7)
      .join('\n');
    const auth = store.getState().auth;
    console.log('[WS-DEBUG] notif.connect() called', {
      fromReconnect: !!options.fromReconnect,
      isAuthenticated: auth.isAuthenticated,
      hasUser: !!auth.user,
      validationState: auth.validationState,
      isConnecting: this.isConnecting,
      isOpen: this.socket?.readyState === WebSocket.OPEN,
      isManuallyDisconnected: this.isManuallyDisconnected,
      reconnectAttempts: this.reconnectAttempts,
      caller: callerStack,
    });

    // Hard guard: never attempt a connection while Redux says the user isn't
    // authenticated. The previous behavior — unconditionally clearing
    // isManuallyDisconnected on every connect() — meant the very next
    // scheduleReconnect() cycle would defeat any disconnect() the hook had
    // just performed. We now (a) refuse to connect at all when not authed,
    // and (b) only reset the manual-disconnect flag when the call is an
    // explicit external request (not a scheduled reconnect).
    if (!isAuthValid()) {
      console.log('[WS-DEBUG] notif.connect() bail: not authenticated', auth);
      return;
    }

    if (!options.fromReconnect) {
      this.isManuallyDisconnected = false;
    }

    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      console.log('[WS-DEBUG] notif.connect() bail: already OPEN');
      return;
    }

    if (this.isConnecting) {
      console.log('[WS-DEBUG] notif.connect() bail: already connecting');
      return;
    }

    this.setConnectionState('connecting');
    this.isConnecting = true;
    
    try {
      // Cleanup existing connection
      this.cleanup();
      
      // Get temporary WebSocket token from server
      const wsToken = await this.getWebSocketToken();
      if (!wsToken) {
        if (DEBUG_NS_VERBOSE) {
          console.warn('[NotificationWS] Failed to get WebSocket token, scheduling retry');
        }
        this.setConnectionState('disconnected');
        this.isConnecting = false;
        this.notifyConnectionHandlers(false);
        this.scheduleReconnect();
        return;
      }

      // P0-C: token via Sec-WebSocket-Protocol subprotocol, not URL path.
      // See websocket.ts for full rationale. Backend echoes the matching
      // `aimepic.token.<value>` subprotocol back on accept (RFC 6455).
      const socketUrl = `${WEBSOCKET_BASE_URL}/notifications/`;
      const subprotocol = `aimepic.token.${wsToken}`;

      // Store URL for debugging
      this.lastSocketUrl = socketUrl;

      if (DEBUG_NS_VERBOSE) {
        console.log('[NotificationWS] Resolved base URL:', WEBSOCKET_BASE_URL);
        console.log('[NotificationWS] wsToken:', wsToken ? '***' : '(none)');
        console.log('[NotificationWS] Final socketUrl:', socketUrl, '(token via subprotocol)');
      }

      // Small delay to prevent WebSocket services from connecting simultaneously
      await new Promise(resolve => setTimeout(resolve, 200));

      // Single-element subprotocol array works on all browsers including Firefox.
      this.socket = new WebSocket(socketUrl, [subprotocol]);
      
      this.setupEventListeners();
      
    } catch (error) {
      if (DEBUG_NS_VERBOSE) {
        console.error('[NotificationWS] Failed to establish connection:', error);
      }
      this.setConnectionState('error');
      this.isConnecting = false;
      this.notifyConnectionHandlers(false);
      this.scheduleReconnect();
    }
  }
  
  private cleanup(): void {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    
    this.isConnecting = false;
  }
  
  private scheduleReconnect(): void {
    // [WS-DEBUG] Always-on: log every reconnect decision.
    const auth = store.getState().auth;
    console.log('[WS-DEBUG] notif.scheduleReconnect()', {
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts,
      isManuallyDisconnected: this.isManuallyDisconnected,
      hasPendingTimeout: !!this.reconnectTimeout,
      authValid: isAuthValid(),
      authState: { isAuthenticated: auth.isAuthenticated, hasUser: !!auth.user, validationState: auth.validationState },
    });

    if (this.isManuallyDisconnected) {
      console.log('[WS-DEBUG] notif.scheduleReconnect bail: manually disconnected');
      return;
    }

    if (this.reconnectTimeout) {
      console.log('[WS-DEBUG] notif.scheduleReconnect bail: already scheduled');
      return;
    }

    // Don't burn reconnect attempts when the user is not authenticated. Stale
    // cookies that briefly let the ws-token endpoint succeed used to keep this
    // singleton looping forever after logout / session invalidation.
    if (!isAuthValid()) {
      console.log('[WS-DEBUG] notif.scheduleReconnect bail: not authenticated');
      this.reconnectAttempts = 0;
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('[WS-DEBUG] notif.scheduleReconnect bail: max attempts reached');
      this.setConnectionState('error');
      return;
    }

    const delay = this.reconnectInterval * Math.pow(2, this.reconnectAttempts);
    console.log('[WS-DEBUG] notif.scheduleReconnect scheduling', {
      attempt: this.reconnectAttempts + 1,
      max: this.maxReconnectAttempts,
      delayMs: delay,
    });

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      this.reconnectAttempts++;
      // fromReconnect: true so connect() does NOT clear isManuallyDisconnected.
      this.connect({ fromReconnect: true });
    }, delay);
  }

  private async verifyUserAuthentication(): Promise<boolean> {
    try {
      const response = await fetchWithAuthRetry(`${API_BASE_URL}/auth/session-status/`, {
        method: 'GET',
      });
      
      if (!response.ok) {
        if (DEBUG_NS_VERBOSE) {
          console.error('[NotificationWS] Authentication verification failed:', response.status, response.statusText);
        }
        return false;
      }
      
      const data = await response.json();
      if (data.authenticated === true) {
        if (DEBUG_NS_VERBOSE) {
          console.log('[NotificationWS] Authentication status: authenticated');
        }
        return true;
      }

      // session-status responds 200 with {authenticated: false} for an
      // expired access cookie (it's a public/AllowAny endpoint by design, so
      // it never 401s) — a coordinated refresh can still recover the session
      // before we give up and skip WebSocket connection entirely.
      const refresh = await coordinatedRefresh();
      if (!refresh.ok) {
        if (DEBUG_NS_VERBOSE) {
          console.log('[NotificationWS] Authentication status: not authenticated (refresh failed)');
        }
        return false;
      }

      const recheck = await fetch(`${API_BASE_URL}/auth/session-status/`, {
        method: 'GET',
        credentials: 'include',
      });
      const recheckData = recheck.ok ? await recheck.json() : {};
      const isAuthenticated = recheckData.authenticated === true;
      if (DEBUG_NS_VERBOSE) {
        console.log('[NotificationWS] Authentication status after refresh:', isAuthenticated ? 'authenticated' : 'not authenticated');
      }
      return isAuthenticated;
    } catch (error) {
      if (DEBUG_NS_VERBOSE) {
        console.error('[NotificationWS] Authentication verification error:', error);
      }
      return false;
    }
  }

  private getCSRFToken(): string {
    const cookieValue = document.cookie
      .split('; ')
      .find(row => row.startsWith('csrftoken='))
      ?.split('=')[1];
    return cookieValue || '';
  }

  private async getWebSocketToken(retryCount: number = 0): Promise<string | null> {
    const maxRetries = 3;
    const retryDelay = 1000; // 1 second
    
    try {
      // Verify user is authenticated before requesting token
      const isAuthenticated = await this.verifyUserAuthentication();
      if (!isAuthenticated) {
        if (DEBUG_NS_VERBOSE) {
          console.error('[NotificationWS] User not authenticated - cannot get WebSocket token');
        }
        return null;
      }

      // The websocket-token endpoint is correctly at /api/auth/websocket-token/
      // fetchWithAuthRetry self-heals an expired access cookie (mobile
      // background resume) via a coordinated refresh + single retry.
      const response = await fetchWithAuthRetry(`${API_BASE_URL}/auth/websocket-token/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.getCSRFToken(),
        },
      });

      // [WS-DEBUG] Always-on: token endpoint result.
      console.log('[WS-DEBUG] notif.getWebSocketToken result', {
        status: response.status,
        ok: response.ok,
        attempt: retryCount + 1,
      });

      if (!response.ok) {
        const errorText = await response.text();
        if (DEBUG_NS_VERBOSE) {
          console.error('[NotificationWS] Failed to get WebSocket token (attempt', retryCount + 1, '/', maxRetries + 1, '):', {
            status: response.status,
            statusText: response.statusText,
            error: errorText
          });
        }
        
        // Retry for certain error conditions
        if (retryCount < maxRetries && (response.status === 503 || response.status === 502 || response.status >= 500)) {
          if (DEBUG_NS_VERBOSE) {
            console.log('[NotificationWS] Retrying token request in', retryDelay, 'ms');
          }
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          return this.getWebSocketToken(retryCount + 1);
        }
        
        return null;
      }

      const data = await response.json();
      if (DEBUG_NS_VERBOSE) {
        if (retryCount > 0) {
          console.log('[NotificationWS] WebSocket token obtained after', retryCount + 1, 'attempts');
        } else {
          console.log('[NotificationWS] WebSocket token obtained successfully');
        }
      }
      return data.ws_token;
    } catch (error) {
      if (DEBUG_NS_VERBOSE) {
        console.error('[NotificationWS] Error getting WebSocket token (attempt', retryCount + 1, '/', maxRetries + 1, '):', error);
      }
      
      // Retry for network errors
      if (retryCount < maxRetries) {
        if (DEBUG_NS_VERBOSE) {
          console.log('[NotificationWS] Retrying token request due to network error in', retryDelay, 'ms');
        }
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        return this.getWebSocketToken(retryCount + 1);
      }
      
      return null;
    }
  }

  private setupEventListeners(): void {
    if (!this.socket) return;

    // Add connection timeout for Firefox
    const connectionTimeout = setTimeout(() => {
      if (this.socket && this.socket.readyState === WebSocket.CONNECTING) {
        if (DEBUG_NS_VERBOSE) {
          console.warn('[NotificationWS] Connection timeout, forcing reconnect');
        }
        this.socket.close();
        this.setConnectionState('error');
        this.scheduleReconnect();
      }
    }, 10000); // 10 second timeout

    this.socket.onopen = () => {
      clearTimeout(connectionTimeout);
      // [WS-DEBUG] Always-on: confirm subprotocol echoed back. If empty,
      // the backend did NOT echo `aimepic.token.<value>` and the browser will
      // close the handshake immediately per RFC 6455.
      console.log('[WS-DEBUG] notif.onopen', {
        protocol: this.socket?.protocol || '(empty — backend did not echo subprotocol)',
        readyState: this.socket?.readyState,
      });
      if (DEBUG_NS_VERBOSE) {
        console.log('[NotificationWS] Connected successfully');
      }
      this.setConnectionState('connected');
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      this.notifyConnectionHandlers(true);
      this.startHeartbeat();
    };

    this.socket.onmessage = (event) => {
      try {
        if (DEBUG_NS_VERBOSE) {
          console.log('[NotificationWS] Raw message received:', event.data);
        }
        const data: WebSocketMessage = JSON.parse(event.data);
        this.handleMessage(data);
      } catch (error) {
        if (DEBUG_NS_VERBOSE) {
          console.error('[NotificationWS] Error parsing message:', error, 'Raw data:', event.data);
        }
      }
    };

    this.socket.onclose = (event) => {
      clearTimeout(connectionTimeout);
      // [WS-DEBUG] Always-on: surface close code + reason on every disconnect.
      // 4001 = disclaimer-not-accepted (P0-A); 4002 = security violation;
      // 4003 = emergency shutdown; 1006 = abnormal close (no close frame).
      console.log('[WS-DEBUG] notif.onclose', {
        code: event.code,
        reason: event.reason || '(none)',
        wasClean: event.wasClean,
        reconnectAttempts: this.reconnectAttempts,
      });
      if (DEBUG_NS_VERBOSE) {
        console.log('[NotificationWS] Disconnected. Code:', event.code, 'Reason:', event.reason || 'No reason provided');
      }
      this.setConnectionState('disconnected');
      this.isConnecting = false;
      this.notifyConnectionHandlers(false);
      this.stopHeartbeat();
      
      // Only attempt reconnection for unexpected closures (not normal disconnect)
      if (event.code !== 1000) {
        // Firefox specific: Handle connection refused differently
        if (navigator.userAgent.toLowerCase().includes('firefox') && event.code === 1006) {
          if (DEBUG_NS_VERBOSE) {
            console.log('[NotificationWS] Firefox connection refused, retrying with delay');
          }
          setTimeout(() => this.scheduleReconnect(), 3000);
        } else {
          this.scheduleReconnect();
        }
      }
    };

    this.socket.onerror = (error) => {
      clearTimeout(connectionTimeout);
      // [WS-DEBUG] Always-on.
      console.log('[WS-DEBUG] notif.onerror', {
        url: this.socket?.url || this.lastSocketUrl || 'undefined',
        readyState: this.socket?.readyState,
      });
      if (DEBUG_NS_VERBOSE) {
        console.error('[NotificationWS] Connection error:', {
          url: this.socket?.url || this.lastSocketUrl || 'undefined',
          readyState: this.socket?.readyState,
          error,
          errorType: (error as Event & { type?: string }).type
        });
      }
      this.setConnectionState('error');
      this.isConnecting = false;
      this.notifyConnectionHandlers(false);
    };
  }

  private handleMessage(data: WebSocketMessage): void {
    if (DEBUG_NS_VERBOSE) {
      console.log('[NotificationWS] Processing message:', data);
    }
    
    switch (data.type) {
      case 'notification':
        if (data.payload?.notification) {
          if (DEBUG_NS_VERBOSE) {
            console.log('[NotificationWS] Processing new notification:', data.payload.notification);
          }
          this.notifyNotificationHandlers(data.payload.notification);
        }
        break;

      case 'notification_update':
        if (DEBUG_NS_VERBOSE) {
          console.log('[NotificationWS] Processing notification update:', data.payload);
        }
        this.notifyUpdateHandlers(data);
        break;

      case 'unread_count':
        if (typeof data.payload?.unread_count === 'number') {
          if (DEBUG_NS_VERBOSE) {
            console.log('[NotificationWS] Unread count update:', data.payload.unread_count);
          }
          this.notifyUnreadCountHandlers(data.payload.unread_count);
        }
        break;

      case 'pong':
        if (DEBUG_NS_VERBOSE) {
          console.log('[NotificationWS] Received heartbeat pong');
        }
        break;

      case 'error':
        if (DEBUG_NS_VERBOSE) {
          console.error('[NotificationWS] Error from server:', data.message);
        }
        break;

      default:
        if (DEBUG_NS_VERBOSE) {
          console.warn('[NotificationWS] Unknown message type:', data.type, data);
        }
    }
  }


  private startHeartbeat(): void {
    this.heartbeatInterval = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify({
          type: 'ping',
          timestamp: Date.now()
        }));
      }
    }, 30000); // Send ping every 30 seconds
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  // Send methods
  markNotificationsAsRead(notificationIds: string[]): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({
        type: 'mark_read',
        notification_ids: notificationIds
      }));
      if (DEBUG_NS_VERBOSE) {
        console.log('[NotificationWS] Sent mark read request:', notificationIds);
      }
    }
  }

  requestUnreadCount(): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({
        type: 'get_unread_count'
      }));
    }
  }

  // Event handler registration methods
  onNotification(handler: NotificationHandler): () => void {
    this.notificationHandlers.push(handler);
    
    // Return unsubscribe function
    return () => {
      const index = this.notificationHandlers.indexOf(handler);
      if (index > -1) {
        this.notificationHandlers.splice(index, 1);
      }
    };
  }

  onUnreadCount(handler: UnreadCountHandler): () => void {
    this.unreadCountHandlers.push(handler);
    
    return () => {
      const index = this.unreadCountHandlers.indexOf(handler);
      if (index > -1) {
        this.unreadCountHandlers.splice(index, 1);
      }
    };
  }

  onConnection(handler: ConnectionHandler): () => void {
    this.connectionHandlers.push(handler);
    
    return () => {
      const index = this.connectionHandlers.indexOf(handler);
      if (index > -1) {
        this.connectionHandlers.splice(index, 1);
      }
    };
  }

  onUpdate(handler: NotificationUpdateHandler): () => void {
    this.updateHandlers.push(handler);
    
    return () => {
      const index = this.updateHandlers.indexOf(handler);
      if (index > -1) {
        this.updateHandlers.splice(index, 1);
      }
    };
  }

  // Handler notification methods
  private notifyNotificationHandlers(notification: NotificationItem): void {
    this.notificationHandlers.forEach(handler => {
      try {
        handler(notification);
      } catch (error) {
        if (DEBUG_NS_VERBOSE) {
          console.error('[NotificationWS] Error in notification handler:', error);
        }
      }
    });
  }

  private notifyUnreadCountHandlers(count: number): void {
    this.unreadCountHandlers.forEach(handler => {
      try {
        handler(count);
      } catch (error) {
        if (DEBUG_NS_VERBOSE) {
          console.error('[NotificationWS] Error in unread count handler:', error);
        }
      }
    });
  }

  private notifyConnectionHandlers(connected: boolean): void {
    this.connectionHandlers.forEach(handler => {
      try {
        handler(connected);
      } catch (error) {
        if (DEBUG_NS_VERBOSE) {
          console.error('[NotificationWS] Error in connection handler:', error);
        }
      }
    });
  }

  private notifyUpdateHandlers(data: any): void {
    this.updateHandlers.forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        if (DEBUG_NS_VERBOSE) {
          console.error('[NotificationWS] Error in update handler:', error);
        }
      }
    });
  }

  // Utility methods
  clearHandlers(): void {
    this.notificationHandlers = [];
    this.unreadCountHandlers = [];
    this.connectionHandlers = [];
    this.updateHandlers = [];
  }

  disconnect(): void {
    if (DEBUG_CONNECTIVITY) {
      console.log('[NotificationWS] Manually disconnecting');
    }
    this.isManuallyDisconnected = true;
    this.cleanup();
    this.setConnectionState('disconnected');
    this.reconnectAttempts = 0;
  }

  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

}

// Create and export singleton instance
const notificationWebSocketService = new NotificationWebSocketService();
export default notificationWebSocketService;
